import { useState } from "react";
import { toast } from "sonner";
import { Clock, Copy, Check, ArrowRight, Zap, Loader2 } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const KIND_TONE = {
  setup: { label: "Setup", cls: "bg-amber-50 text-amber-700" },
  outreach: { label: "Conversation", cls: "bg-teal-light text-teal" },
  campaign: { label: "Campaign", cls: "bg-lime/25 text-navy" },
  compliance: { label: "Compliance", cls: "bg-linen text-ink-600" },
};

const focusTarget = (target) => {
  const el = document.querySelector(`[data-testid="${target}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const TodayActionCard = ({ action, onDone, onLog, busy }) => {
  const [copied, setCopied] = useState(false);

  if (!action) {
    return (
      <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-8" data-testid="today-action-empty">
        <h2 className="font-heading font-medium text-2xl text-navy">You're clear for today.</h2>
        <p className="mt-2 text-sm text-ink-500">
          Everything on your plan is done. Something new will be waiting tomorrow morning.
        </p>
      </div>
    );
  }

  const tone = KIND_TONE[action.kind] || KIND_TONE.setup;

  const copy = async () => {
    const ok = await copyText(action.script);
    if (!ok) return toast.error("Couldn't copy that — select the text instead.");
    setCopied(true);
    toast.success("Message copied — paste it into your text or email");
  };

  return (
    <div className="rounded-3xl bg-navy-deep text-white shadow-soft overflow-hidden" data-testid="today-action-card">
      <div className="p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-lime font-bold">
            <Zap size={13} /> Today's move
          </span>
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-bold ${tone.cls}`}>
            {tone.label}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-white/50">
            <Clock size={12} /> about {action.minutes} min
          </span>
        </div>

        <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl leading-[1.1]" data-testid="today-action-title">
          {action.title}
        </h2>
        <p className="mt-3 text-[15px] text-white/70 leading-relaxed max-w-2xl">{action.why}</p>

        {action.script && (
          <div className="mt-6 rounded-2xl bg-white/[0.06] border border-white/10 p-5" data-testid="today-action-script">
            <p className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Say this</p>
            <p className="mt-2.5 text-[14.5px] text-white/90 leading-relaxed whitespace-pre-line">
              {action.script}
            </p>
            <p className="mt-3 text-[11px] text-white/40">
              Approved wording — no rates quoted, no advice given. Edit the name, not the promise.
            </p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2.5">
          {action.script && (
            <button
              onClick={copy}
              data-testid="today-action-copy"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-navy-deep font-bold text-sm px-6 py-3 hover:brightness-105 transition-all"
            >
              {copied ? <Check size={15} /> : <Copy size={15} />} {action.cta.label}
            </button>
          )}

          {action.cta.type === "link" && (
            <a
              href={action.cta.path}
              data-testid="today-action-link"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-navy-deep font-bold text-sm px-6 py-3 hover:brightness-105 transition-all"
            >
              {action.cta.label} <ArrowRight size={15} />
            </a>
          )}

          {action.cta.type === "focus" && (
            <button
              onClick={() => focusTarget(action.cta.target)}
              data-testid="today-action-focus"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-navy-deep font-bold text-sm px-6 py-3 hover:brightness-105 transition-all"
            >
              {action.cta.label} <ArrowRight size={15} />
            </button>
          )}

          {action.cta.type === "log" && (
            <button
              onClick={onLog}
              data-testid="today-action-log"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-navy-deep font-bold text-sm px-6 py-3 hover:brightness-105 transition-all"
            >
              {action.cta.label} <ArrowRight size={15} />
            </button>
          )}

          <button
            onClick={() => onDone(action, { log: Boolean(action.contact) })}
            disabled={busy}
            data-testid="today-action-done"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white font-semibold text-sm px-6 py-3 hover:bg-white/10 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {action.contact ? "Sent it" : "Done"}
          </button>
        </div>

        {action.contact && (
          <p className="mt-3 text-[11px] text-white/40">
            Marking this sent logs it to your month-end report automatically.
          </p>
        )}
      </div>
    </div>
  );
};

export default TodayActionCard;
